import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  AlertCircle, 
  ExternalLink, 
  CheckCircle, 
  EyeOff,
  Search,
  Filter,
  Trash2,
  Edit,
  XCircle,
  MoreVertical
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link } from "@tanstack/react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FornecedorForm } from "@/components/admin/fornecedor-form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/admin/denuncias")({
  component: DenunciasAdmin,
});

const MOTIVOS = [
  "Telefone ou WhatsApp incorreto",
  "Site fora do ar ou errado",
  "Instagram incorreto",
  "Empresa não existe mais",
  "Não é fornecedor / não vende no atacado",
  "Segmento errado",
  "Informação de endereço incorreta",
  "Conteúdo impróprio",
  "Outro",
];

function DenunciasAdmin() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [currentTab, setCurrentTab] = useState("aberta");
  const [editingFornecedor, setEditingFornecedor] = useState<any>(null);

  const { data: denuncias, isLoading } = useQuery({
    queryKey: ["admin-denuncias", currentTab, search],
    queryFn: async () => {
      let query = supabase
        .from("denuncias")
        .select(`
          *,
          fornecedores(id, nome, status),
          profiles!denuncias_user_id_fkey(nome, email)
        `)
        .eq("status", currentTab)
        .order("created_at", { ascending: false });

      if (search) {
        // As we can't easily join-filter in Supabase JS without advanced config, we'll fetch and filter if search exists
        // But for performance, let's try a simple ilike on motivo at least
        query = query.ilike("motivo", `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data as any[];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: string }) => {
      const { data, error } = await supabase
        .from("denuncias")
        .update({ 
          status,
          resolvido_por: user?.id,
          resolvido_em: new Date().toISOString()
        })
        .eq("id", id)
        .select();

      if (error) {
        console.error("Erro ao atualizar denúncia:", error);
        throw error;
      }
      return { success: (data?.length || 0) > 0 };
    },
    onSuccess: (data) => {
      if (!data.success) {
        toast.warning("Nenhuma denúncia foi alterada. Verifique as permissões.");
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-denuncias"] });
        toast.success("Status da denúncia atualizado!");
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao atualizar denúncia"}`);
    }
  });

  const despublicarMutation = useMutation({
    mutationFn: async (fornecedorId: string) => {
      const { data, error } = await supabase
        .from("fornecedores")
        .update({ status: "rejeitado", motivo_rejeicao: "Desativado devido a denúncias verificadas." })
        .eq("id", fornecedorId)
        .select();

      if (error) {
        console.error("Erro ao despublicar fornecedor:", error);
        throw error;
      }
      return { success: (data?.length || 0) > 0 };
    },
    onSuccess: (data) => {
      if (!data.success) {
        toast.warning("Não foi possível despublicar. Verifique as permissões.");
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-denuncias"] });
        toast.success("Fornecedor despublicado!");
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao despublicar fornecedor"}`);
    }
  });

  // Group by supplier
  const groupedDenuncias = denuncias?.reduce((acc: any[], current) => {
    const existing = acc.find(a => a.fornecedor_id === current.fornecedor_id);
    if (existing) {
      existing.items.push(current);
    } else {
      acc.push({
        fornecedor_id: current.fornecedor_id,
        fornecedor: current.fornecedores,
        items: [current]
      });
    }
    return acc;
  }, []);

  return (
    <div className="p-4 md:p-8 space-y-6 pb-24 md:pb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Gerenciar Denúncias</h1>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Buscar por motivo..." 
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="bg-white border">
          <TabsTrigger value="aberta" className="gap-2">
            Abertas
            {currentTab === 'aberta' && denuncias && denuncias.length > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5 min-w-[20px] justify-center">
                {denuncias.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolvida">Resolvidas</TabsTrigger>
          <TabsTrigger value="descartada">Descartadas</TabsTrigger>
        </TabsList>

        <TabsContent value={currentTab} className="mt-6 space-y-6">
          {isLoading ? (
            <div className="text-center py-20 bg-white border rounded-xl">Carregando denúncias...</div>
          ) : !groupedDenuncias || groupedDenuncias.length === 0 ? (
            <div className="text-center py-20 bg-white border rounded-xl text-muted-foreground">
              Nenhuma denúncia encontrada nesta categoria.
            </div>
          ) : (
            groupedDenuncias.map((group) => (
              <div key={group.fornecedor_id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="bg-secondary/20 p-4 border-b flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Link 
                      to={`/fornecedores/${group.fornecedor_id}` as any}
                      className="text-lg font-bold hover:underline flex items-center gap-2"
                    >
                      {group.fornecedor?.nome || "Fornecedor desconhecido"}
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </Link>
                    {group.items.length > 1 && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {group.items.length} denúncias
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={async () => {
                        const { data } = await supabase.from('fornecedores').select('*').eq('id', group.fornecedor_id).single();
                        setEditingFornecedor(data);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" /> Editar Fornecedor
                    </Button>
                    {group.fornecedor?.status !== 'rejeitado' && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => despublicarMutation.mutate(group.fornecedor_id)}
                      >
                        <EyeOff className="w-4 h-4 mr-2" /> Despublicar
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="divide-y">
                  {group.items.map((d: any) => (
                    <div key={d.id} className="p-4 flex flex-col md:flex-row md:items-start justify-between gap-4 hover:bg-secondary/5 transition-colors">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-brand-dark">{d.motivo}</span>
                          <span className="text-xs text-muted-foreground">• {format(new Date(d.created_at), "dd/MM/yyyy HH:mm")}</span>
                        </div>
                        {d.descricao && (
                          <p className="text-sm text-muted-foreground bg-secondary/10 p-2 rounded-lg border border-secondary/20 italic">
                            "{d.descricao}"
                          </p>
                        )}
                        <div className="text-xs text-muted-foreground">
                          Reportado por: <span className="font-medium text-foreground">{d.profiles?.nome}</span> ({d.profiles?.email})
                        </div>
                      </div>

                      {d.status === 'aberta' && (
                        <div className="flex gap-2 shrink-0">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => updateStatusMutation.mutate({ id: d.id, status: "resolvida" })}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Marcar como resolvida
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => updateStatusMutation.mutate({ id: d.id, status: "descartada" })}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Descartar
                          </Button>
                        </div>
                      )}

                      {(d.status === 'resolvida' || d.status === 'descartada') && (
                        <div className="text-xs text-right text-muted-foreground">
                          {d.status === 'resolvida' ? 'Resolvida' : 'Descartada'} em {d.resolvido_em ? format(new Date(d.resolvido_em), "dd/MM/yy") : "-"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!editingFornecedor} onOpenChange={() => setEditingFornecedor(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl">
          {editingFornecedor && (
            <FornecedorForm 
              initialData={editingFornecedor}
              onCancel={() => setEditingFornecedor(null)}
              onSuccess={() => {
                setEditingFornecedor(null);
                queryClient.invalidateQueries({ queryKey: ["admin-denuncias"] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
